import React from 'react';
import { Phone, Mail, MapPin, Facebook, Instagram, MessageCircle, Heart } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const Footer = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: t('nav.home'), href: '#home' },
    { name: t('nav.services'), href: '#services' },
    { name: t('nav.packages'), href: '#packages' },
    { name: t('nav.blog'), href: '#blog' },
    { name: t('nav.contact'), href: '#contact' }
  ];

  const services = [
    t('packages.essential.name').split(' ').slice(0, 2).join(' '),
    t('packages.rental.name').split(' ').slice(0, 2).join(' '),
    t('packages.premium.name').split(' ').slice(0, 2).join(' '),
    t('features.services.deepCleaning'),
    t('features.services.poolMaintenance'),
    t('features.services.airportTransfers')
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="footer-decoration bg-[#E0F4FF] text-[#000080]">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <img
                src={companyInfo.logo}
                alt="Peace of Mind"
                className="h-20 w-auto"
              />
            </div>
            <p className="text-[#000080]/70 mb-6 text-sm leading-relaxed">
              {t('footer.description')}
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/profile.php?id=61588266478252"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#FF6B35] flex items-center justify-center hover:bg-[#FF6B35]/80 text-white transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/pompm809"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#8B5CF6] flex items-center justify-center hover:bg-[#8B5CF6]/80 text-white transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://wa.me/18292852485"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#22C55E] flex items-center justify-center hover:bg-[#22C55E]/80 text-white transition-colors"
              >
                <MessageCircle className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-6 text-[#000080]">{t('footer.quickLinks')}</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-[#000080]/70 hover:text-[#000080] transition-colors"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-bold text-lg mb-6 text-[#000080]">{t('footer.services')}</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <span className="text-[#000080]/70">{service}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-bold text-lg mb-6 text-[#000080]">{t('footer.contactUs')}</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#000080] flex-shrink-0 mt-0.5" />
                <span className="text-[#000080]/70">{companyInfo.location}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#000080] flex-shrink-0" />
                <a
                  href="https://wa.me/18292852485"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#000080]/70 hover:text-[#000080] transition-colors"
                >
                  {companyInfo.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#000080] flex-shrink-0" />
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="text-[#000080]/70 hover:text-[#000080] transition-colors"
                >
                  {companyInfo.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-[#000080]/10 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#000080]/50 text-sm text-center md:text-left">
              © {currentYear} Peace of Mind Property Management. {t('footer.rights')}
            </p>
            <p className="text-[#000080]/50 text-sm flex items-center gap-1">
              {t('footer.madeWith')} <Heart className="w-4 h-4 text-[#000080] fill-[#000080]" /> {t('footer.inPuertoPlata')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
